import NiceModal from "@ebay/nice-modal-react";
import { type NodeApi } from "@hogartrabe/react-arborist-vrh";
import cx from "classnames";
import { Product3dViewModal } from "components/_Modals/Product3dViewModal/Product3dViewModal";
import { SingleProductDownloadModal } from "components/_Modals/ProjectDownloadModal/SingleProductDownloadModal";
import { CommissioningCostNode } from "components/ProjectTree/components/Configuration/CommissioningCostNode";
import { ProductDetails } from "components/ProjectTree/components/Configuration/ProductDetails";
import { ProductImage } from "components/ProjectTree/components/Configuration/ProductImage";
import type { defaultOptions } from "variables/nodeDefaultOptions";
import {
  useDeleteConfirmationCheck,
  type useProjectTreeContentTranslations,
} from "hooks/projectTreeHooks";
import {
  type NavigateToCustomProductProps,
  type OrderCodeRequestSlice,
  type NavigateToProductProps,
} from "interfaces/product/product.types";
import type { Node } from "interfaces/schemas";
import React, { useCallback } from "react";
import {
  useAddedProducts,
  useCopiedItem,
  useProject,
  useProjectActions,
} from "stores/project";
import * as identify from "utils/treeUtils";
import type { ProductStateInput } from "stores/product";
import { Unit } from "variables/enums";
import s from "../Styles.module.scss";
import { ConfigurationControls } from "./ConfigurationControls";

type ConfigurationProps = {
  node: NodeApi<Node>;
  options: typeof defaultOptions;
  translations: ReturnType<typeof useProjectTreeContentTranslations>;
  handleToggleEditMode: (type: string | boolean) => void;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean | string>>;
  isEditMode: boolean | string;
  lang: string;
  navigateToProduct: (props: NavigateToProductProps) => void;
  navigateToCustomProduct: (props: NavigateToCustomProductProps) => void;
};

export const Configuration = ({
  node,
  options,
  translations,
  navigateToProduct,
  handleToggleEditMode,
  setIsEditMode,
  isEditMode,
  lang,
  navigateToCustomProduct,
}: ConfigurationProps) => {
  const actions = useProjectActions();
  const project = useProject();
  const copiedItem = useCopiedItem();
  const deleteConfirmationCheck = useDeleteConfirmationCheck();
  const addedProducts = useAddedProducts();
  const { data } = node;
  const isPasteMode = !!copiedItem;

  const canDeleteInAddToProjectModal =
    !!options?.productAddingModal && addedProducts?.includes(data?.id);

  const handleDelete = useCallback(() => {
    deleteConfirmationCheck(node, () => actions.tree.deleteNode(node));
  }, [deleteConfirmationCheck, node, actions]);

  const handleToggle3dView = useCallback(() => {
    if (
      !data ||
      (!identify.isProduct(data) &&
        !identify.isWizardProduct(data) &&
        !identify.isAnyAccessory(data))
    ) {
      return;
    }

    const seriesId = data.orderCode?.seriesId;
    if (seriesId === null) return;

    const currentStrategyId = data.orderCode?.currentStrategyId;
    if (!currentStrategyId) {
      console.error("Current strategy ID is missing. Cannot open 3D view.");
      return;
    }

    NiceModal.show(Product3dViewModal, {
      requestParams: {
        seriesId: String(seriesId),
        state: {
          orderCodeState: data.orderCode
            ?.state as ProductStateInput["orderCodeState"],
          currentStrategyId,
        },
        resultUnitsSystem: project?.unit.unit || Unit.de_m3ph,
        languageIso2L: lang,
      },
    });
  }, [lang, data, project?.unit]);

  const openDownloadModal = useCallback(() => {
    if (
      !data ||
      (!identify.isProduct(data) &&
        !identify.isWizardProduct(data) &&
        !identify.isAnyAccessory(data))
    ) {
      return;
    }
    NiceModal.show(SingleProductDownloadModal, {
      modalSubtitle: data.productName,
      orderCode: (data?.orderCode ?? undefined) as
        | OrderCodeRequestSlice
        | undefined,
      presentation: data?.presentation ?? "",
      nodeId: data?.id,
    });
  }, [data]);

  if (!identify.isAnyProduct(data)) {
    return null;
  }
  const isCommissioningCost = identify.isWizardCommissioningCost(data);

  return (
    <div className="d-flex h-100 w-100 flex-row align-items-center justify-content-between overflow-hidden">
      <div
        className="d-flex flex-row align-items-center gap-2 h-100 overflow-hidden w-100"
        style={{ minWidth: 0 }}
      >
        {!isCommissioningCost && identify.isAnyProduct(data) && (
          <ProductImage
            pimId={data.pimId ?? ""}
            productImagesDisabled={options.productImagesDisabled}
            productName={data.productName ?? ""}
            width={64}
            height={64}
          />
        )}
        {isCommissioningCost ? (
          <CommissioningCostNode
            nodeId={data.parentId}
            isActive={data.active}
          />
        ) : (
          <ProductDetails
            options={options}
            productName={data.productName ?? ""}
            presentation={data.presentation}
            navigateToProduct={navigateToProduct}
            isPasteMode={isPasteMode}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            handleUpdateProductName={(productName: string) =>
              actions.tree.updateNode(node, {
                productName,
                presentation: data?.presentation,
                ...(identify.isCustomProduct(data)
                  ? {
                      price: data.price,
                      priceGroup: data.priceGroup,
                      productDescription: data.productDescription,
                    }
                  : {}),
              })
            }
            handleToggleEditMode={handleToggleEditMode}
            navigateToCustomProduct={navigateToCustomProduct}
            data={data}
          />
        )}
      </div>
      {!isCommissioningCost && (
        <div
          className={cx({
            [s.configControlsContainer]: options.isFullpage,
          })}
        >
          <ConfigurationControls
            configId={node?.data?.id}
            isPasteMode={isPasteMode}
            handleToggleEditMode={handleToggleEditMode}
            isEditMode={isEditMode}
            handleCopyNode={() => actions.tree.copyNode(node)}
            handleDelete={handleDelete}
            handleUpdateQuantity={(quantity: number) =>
              actions.tree.updateNode(node, {
                quantity,
                presentation: data?.presentation,
                ...(identify.isCustomProduct(data)
                  ? {
                      productName: data.productName,
                      price: data.price,
                      priceGroup: data.priceGroup,
                      productDescription: data.productDescription,
                    }
                  : {}),
              })
            }
            openDownloadModal={openDownloadModal}
            options={options}
            translations={translations}
            data={data}
            handleToggle3dView={handleToggle3dView}
            onClickOutside={() => setIsEditMode(false)}
            canDeleteInAddToProjectModal={canDeleteInAddToProjectModal}
          />
        </div>
      )}
    </div>
  );
};
